/**
 * Lifecycle stages.
 *
 * This file is the single definition of "where is this person". It is pure:
 * no database, no HTTP, no framework. Everything else in the system — the
 * pipeline view, the buckets, the Phase 2 messaging triggers — reads stage
 * from here rather than re-deriving it, so there is exactly one place to
 * change when the thresholds get tuned against real data.
 */

export const STAGE_IDS = [
  'lead',
  'paid_not_activated',
  'activated_not_started',
  'onboarding',
  'training_active',
  'at_risk',
  'lapsed',
  'completed',
  'churned_refunded',
] as const

export type StageId = (typeof STAGE_IDS)[number]

export type StageSeverity = 'critical' | 'serious' | 'warning' | 'good' | null

export interface StageMeta {
  id: StageId
  label: string
  /** Plain-language definition, shown in the UI so nobody has to read this file. */
  definition: string
  severity: StageSeverity
  /** Display order in the pipeline view. */
  order: number
}

export const STAGES: readonly StageMeta[] = [
  { id: 'lead', label: 'Lead', definition: 'Asked about the product, never paid', severity: null, order: 1 },
  { id: 'paid_not_activated', label: 'Paid · no account', definition: 'Payment taken, no app account created', severity: 'critical', order: 2 },
  { id: 'activated_not_started', label: 'Account · 0 sessions', definition: 'Account exists, has never trained', severity: 'serious', order: 3 },
  { id: 'onboarding', label: 'Onboarding', definition: '1–4 sessions completed', severity: 'warning', order: 4 },
  { id: 'training_active', label: 'Training active', definition: '5+ sessions, trained recently', severity: 'good', order: 5 },
  { id: 'at_risk', label: 'At risk', definition: 'No session for 8–21 days', severity: 'serious', order: 6 },
  { id: 'lapsed', label: 'Lapsed', definition: 'No session for 21+ days, under the protocol target', severity: 'critical', order: 7 },
  { id: 'completed', label: 'Protocol complete', definition: '30+ sessions completed', severity: 'good', order: 8 },
  { id: 'churned_refunded', label: 'Refunded', definition: 'Refunded or cancelled', severity: null, order: 9 },
] as const

export const STAGE_BY_ID: Readonly<Record<StageId, StageMeta>> = Object.fromEntries(
  STAGES.map((s) => [s.id, s]),
) as Record<StageId, StageMeta>

/**
 * Thresholds live in config, not in the branching below, because you will
 * want to tune them once the real distribution of session gaps is visible.
 */
export interface StageThresholds {
  /** Sessions that count as "past onboarding". */
  onboardingSessions: number
  /** Sessions that complete the protocol. */
  protocolSessions: number
  /** A session within this many days means currently active. */
  activeWithinDays: number
  /** Beyond activeWithinDays but within this many days means at risk. */
  atRiskWithinDays: number
}

export const DEFAULT_THRESHOLDS: StageThresholds = {
  onboardingSessions: 5,
  protocolSessions: 30,
  activeWithinDays: 7,
  atRiskWithinDays: 21,
}

/** The minimum set of facts needed to place someone. */
export interface StageFacts {
  paidAt: Date | null
  refundedAt: Date | null
  /** App account exists in the Oryx replica. */
  hasAppAccount: boolean
  sessionsTotal: number
  lastSessionAt: Date | null
}

export function computeStage(
  facts: StageFacts,
  now: Date = new Date(),
  thresholds: StageThresholds = DEFAULT_THRESHOLDS,
): StageId {
  if (facts.refundedAt) return 'churned_refunded'

  /**
   * A lead is someone we have neither payment nor an account for. An Oryx
   * account on its own is enough to be past this point: the account is not
   * created until someone is training or about to, so treating an unpaid
   * account-holder as a waitlist lead described a person who does not exist.
   *
   * This matters beyond one label. Everyone arriving from the Oryx replica has
   * an account and no payment date — orders live in Wix — so the old rule
   * parked the entire training population at `lead`, and every stage-based
   * bucket read zero.
   */
  if (!facts.paidAt && !facts.hasAppAccount) return 'lead'
  if (!facts.hasAppAccount) return 'paid_not_activated'
  if (facts.sessionsTotal <= 0) return 'activated_not_started'
  if (facts.sessionsTotal >= thresholds.protocolSessions) return 'completed'

  const daysSince = facts.lastSessionAt ? daysBetween(facts.lastSessionAt, now) : Number.POSITIVE_INFINITY

  if (daysSince <= thresholds.activeWithinDays) {
    return facts.sessionsTotal >= thresholds.onboardingSessions ? 'training_active' : 'onboarding'
  }
  if (daysSince <= thresholds.atRiskWithinDays) return 'at_risk'
  return 'lapsed'
}

export function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / 86_400_000)
}

/**
 * Where somebody is in their life with us, from the facts the stage already
 * encodes: a lead has asked and not paid, a customer has paid and not started
 * training, a patient has trained. A refund ends it, whatever came before.
 * Every finer stage belongs to exactly one of these.
 */
export type Lifecycle = 'lead' | 'customer' | 'patient' | 'refunded'

export const LIFECYCLE_LABELS: Record<Lifecycle, string> = {
  lead: 'Lead',
  customer: 'Customer',
  patient: 'Patient',
  refunded: 'Refunded',
}

export function lifecycleOf(stage: StageId): Lifecycle {
  switch (stage) {
    case 'lead': return 'lead'
    case 'paid_not_activated':
    case 'activated_not_started': return 'customer'
    case 'churned_refunded': return 'refunded'
    default: return 'patient'
  }
}
